import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "src/shadcn/ui/button";

interface PaginationProps {
  nextPageToken?: string;
  prevPageTokens: string[];
  setPrevPageTokens: React.Dispatch<React.SetStateAction<string[]>>;
  setPageToken: React.Dispatch<React.SetStateAction<string | undefined>>;
  totalPages?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  nextPageToken,
  prevPageTokens,
  setPrevPageTokens,
  setPageToken,
  totalPages
}) => {
  const currentPage = prevPageTokens.length + 1;

  const handleNextPage = () => {
    setPrevPageTokens((prevTokens) => (nextPageToken ? [...prevTokens, nextPageToken] : prevTokens));
    setPageToken(nextPageToken);
  };

  const handlePrevPage = () => {
    setPrevPageTokens((prevTokens) => {
      if (prevTokens.length > 1) {
        const updatedPrevTokens = prevTokens.slice(0, -1);
        setPageToken(updatedPrevTokens[updatedPrevTokens.length - 1]);
        return updatedPrevTokens;
      }
      setPageToken(undefined);
      return [];
    });
  };

  const handleJumpToPage = (pageIndex: number) => {
    if (pageIndex === 0) {
      setPageToken(undefined);
      setPrevPageTokens([]);
    } else if (pageIndex < prevPageTokens.length) {
      const updatedPrevTokens = prevPageTokens.slice(0, pageIndex);
      setPageToken(prevPageTokens[pageIndex - 1]);
      setPrevPageTokens(updatedPrevTokens);
    }
  };

  const isPrevDisabled = currentPage === 1;
  const isNextDisabled = !nextPageToken;

  const getVisiblePageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    if (currentPage <= maxVisiblePages) {
      for (let i = 1; i <= currentPage; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      const startPage = currentPage - 2;
      for (let i = startPage; i <= currentPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePageNumbers();

  return (
    <div className="flex items-center justify-center gap-2">
      <Button
        variant="outline"
        size="default"
        onClick={handlePrevPage}
        disabled={isPrevDisabled}
        className="h-9 px-3 gap-1 bg-background border-secondary-200 hover:bg-secondary-50 hover:border-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="hidden sm:inline text-sm">Previous</span>
      </Button>

      <div className="flex items-center gap-1">
        {visiblePages.map((pageNum, index) => {
          const isCurrentPage = pageNum === currentPage;
          const isClickable = !isCurrentPage;
          const showEllipsis = index > 0 && pageNum - visiblePages[index - 1] > 1;

          return (
            <React.Fragment key={pageNum}>
              {showEllipsis && <span className="px-2 text-sm text-muted-600">...</span>}
              <Button
                variant={isCurrentPage ? "default" : "outline"}
                size="sm"
                onClick={() => isClickable && handleJumpToPage(pageNum - 1)}
                disabled={isCurrentPage}
                className={`h-9 min-w-[36px] px-2 text-sm ${
                  isCurrentPage
                    ? "bg-secondary-900 text-background hover:bg-secondary-800"
                    : "bg-background border-secondary-200 hover:bg-secondary-50 hover:border-secondary-300"
                } ${isCurrentPage ? "disabled:opacity-100 disabled:cursor-default" : ""} transition-colors`}
              >
                {pageNum}
              </Button>
            </React.Fragment>
          );
        })}
        {totalPages && <span className="px-2 text-sm text-muted-600">of {totalPages}</span>}
      </div>

      <Button
        variant="outline"
        size="default"
        onClick={handleNextPage}
        disabled={isNextDisabled}
        className="h-9 px-3 gap-1 bg-background border-secondary-200 hover:bg-secondary-50 hover:border-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <span className="hidden sm:inline text-sm">Next</span>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

export { Pagination };
